import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import {
    Box, Button, IconButton, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
} from '@mui/material'
import { ArrowForwardRounded } from '@mui/icons-material'
import { H3 } from '../../../layouts/TextStyles'
import { tableStyles, ui } from '../../../utils/uiTokens'
import { ratingApiSlice } from '../../../features/rating/ratingSlice'
import { StudentRatingState } from '../../../features/types'
import { LEVEL_COLORS } from '../../curator/ratingPage/ratingLevel'
import { HeadFilters, groupInScope, useHeadFilters, useHeadIndex } from '../headData'
import { useHeadScope } from '../headScope'

// Аптасына 2 ПС: ПС 1 и ПС 2, в месяце 8. Отметки ставит ментор в «Практика және зачет».
const WEEKS = [1, 2, 3, 4]
const attended = (status?: string) => status === 'WITH_CAMERA' || status === 'WITHOUT_CAMERA'

interface Rec {
    key: number
    userCourseEnrollId: number
    name: string
    groupId: number
    marks: boolean[]
    count: number
    pct: number
    video: number
}

type Filter = 'all' | 'attended' | 'below50' | 'none'
const FILTERS: Filter[] = ['all', 'attended', 'below50', 'none']
const passes = (r: Rec, f: Filter) =>
    f === 'all' || (f === 'none' ? r.count === 0 : f === 'below50' ? r.count > 0 && r.pct < 50 : r.pct >= 50)

const pctColor = (pct: number) => (pct >= 85 ? LEVEL_COLORS.green : pct >= 50 ? LEVEL_COLORS.yellow : LEVEL_COLORS.red)

const HeadPracticePage = () => {
    const { formatMessage } = useIntl()
    const navigate = useNavigate()
    const f = useHeadFilters()
    const { basePath } = useHeadScope()
    const index = useHeadIndex()
    const [tab, setTab] = useState<'mentors' | 'students' | 'top'>('mentors')
    const [mentorId, setMentorId] = useState<number | null>(null)
    const [filter, setFilter] = useState<Filter>('all')
    const [search, setSearch] = useState('')
    const [sortAsc, setSortAsc] = useState(true)

    const month = f.monthNumber || 0
    const week = f.weekNumber || 0
    // неделя - один запрос; месяц - по запросу на каждую неделю, чтобы видеть обе ПС каждой недели
    const useWeek = (w: number) =>
        ratingApiSlice.useGetStudentRatingQuery(
            { groupId: f.groupId || 0, monthNumber: month, weekNumber: w, filter: '', page: 0, size: 1000 },
            { skip: !month || (week ? w !== week : false) },
        )
    const w1 = useWeek(1)
    const w2 = useWeek(2)
    const w3 = useWeek(3)
    const w4 = useWeek(4)
    const byWeek = [w1, w2, w3, w4]
    const loading = byWeek.some((r) => r.isFetching) || index.isFetching
    const weeks = week ? [week] : WEEKS

    const records = useMemo<Rec[]>(() => {
        const map = new Map<number, { row: StudentRatingState; perWeek: Map<number, StudentRatingState> }>()
        weeks.forEach((w) => {
            ;(byWeek[w - 1].data?.data ?? []).forEach((row) => {
                if (!groupInScope(index, f, row.groupId)) return
                const entry = map.get(row.userSubjectEnrollId) ?? { row, perWeek: new Map() }
                entry.perWeek.set(w, row)
                map.set(row.userSubjectEnrollId, entry)
            })
        })
        return Array.from(map.values()).map(({ row, perWeek }) => {
            const marks = weeks.flatMap((w) => {
                const r = perWeek.get(w)
                return [attended(r?.practiceStatus), attended(r?.practice2Status)]
            })
            const count = marks.filter(Boolean).length
            const rows = Array.from(perWeek.values())
            const done = rows.reduce((s, r) => s + (r.videoDone || 0), 0)
            const total = rows.reduce((s, r) => s + (r.videoTotal || 0), 0)
            return {
                key: row.userSubjectEnrollId,
                userCourseEnrollId: row.userCourseEnrollId,
                name: `${row.firstName} ${row.lastName}`,
                groupId: row.groupId || 0,
                marks,
                count,
                pct: Math.round((count * 100) / marks.length),
                video: total ? Math.round((done * 100) / total) : 0,
            }
        })
    }, [w1.data, w2.data, w3.data, w4.data, index, f.courseId, f.groupId, week])

    const mentors = useMemo(
        () =>
            index.mentors
                .map((m) => ({ ...m, recs: records.filter((r) => m.groupIds.includes(r.groupId)) }))
                .filter((m) => m.recs.length),
        [index, records],
    )

    const summary = (recs: Rec[]) => {
        const n = recs.length
        const ps = (i: 0 | 1) => {
            // ПС 1 - чётные отметки, ПС 2 - нечётные; за месяц считаем по всем неделям
            let hit = 0
            let all = 0
            recs.forEach((r) => r.marks.forEach((v, idx) => {
                if (idx % 2 === i) {
                    all += 1
                    if (v) hit += 1
                }
            }))
            return { hit: week ? hit : Math.round(hit / weeks.length), pct: all ? Math.round((hit * 100) / all) : 0 }
        }
        return {
            n,
            ps1: ps(0),
            ps2: ps(1),
            full: recs.filter((r) => r.count === r.marks.length && r.marks.length > 0).length,
            none: recs.filter((r) => r.count === 0).length,
            avg: n ? Math.round(recs.reduce((s, r) => s + r.pct, 0) / n) : 0,
        }
    }

    const groupLabel = (groupId: number) => `${index.groupTitle.get(groupId) ?? ''} · ${index.mentorNames(groupId)}`
    const openStudent = (r: Rec) => navigate(`${basePath}/students/${r.userCourseEnrollId}`)
    const sorted = (recs: Rec[]) =>
        recs
            .filter((r) => passes(r, filter))
            .filter((r) => !search || r.name.toLowerCase().includes(search.toLowerCase()))
            .sort((a, b) => (sortAsc ? a.pct - b.pct : b.pct - a.pct))
    const byAttendance = (a: Rec, b: Rec) => b.pct - a.pct || b.video - a.video

    const selected = mentors.find((m) => m.mentorId === mentorId)

    const marksCell = (r: Rec) => (
        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {r.marks.map((v, i) => (
                <Mark key={i} on={v} gap={!week && i % 2 === 1 && i < r.marks.length - 1} />
            ))}
        </Box>
    )

    const filterChips = (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {FILTERS.map((x) => (
                <Box key={x} sx={chip(filter === x)} onClick={() => setFilter(x)}>
                    {formatMessage({ id: `practiceFilter_${x}` })}
                </Box>
            ))}
        </Box>
    )

    return (
        <Box sx={{ margin: '10px' }}>
            <Box sx={page}>
                <H3>{formatMessage({ id: 'headPractice' })}</H3>
                <Box sx={{ color: ui.sub, fontSize: '13.5px', margin: '6px 0 18px' }}>{formatMessage({ id: 'headPracticeSub' })}</Box>
                <HeadFilters filters={f} index={index} />

                {!month ? (
                    <Box sx={{ color: ui.muted, padding: '24px 0' }}>{formatMessage({ id: 'headPickMonth' })}</Box>
                ) : selected ? (
                    <>
                        <Button onClick={() => setMentorId(null)} sx={{ color: ui.sub, paddingLeft: 0 }}>← {formatMessage({ id: 'salaryBack' })}</Button>
                        <Box sx={{ fontWeight: 700, fontSize: '18px', margin: '4px 0 14px' }}>{selected.name}</Box>
                        {(() => {
                            const s = summary(selected.recs)
                            return (
                                <Box sx={cards}>
                                    <Card label={formatMessage({ id: 'colStudents' })} value={`${s.n}`} />
                                    <Card label={formatMessage({ id: 'practicePs1' })} value={`${s.ps1.pct}%`} />
                                    <Card label={formatMessage({ id: 'practicePs2' })} value={`${s.ps2.pct}%`} />
                                    <Card label={formatMessage({ id: 'practiceFull' })} value={`${s.full}`} />
                                    <Card label={formatMessage({ id: 'practiceAvg' })} value={`${s.avg}%`} />
                                    <Card label={formatMessage({ id: 'practiceNone' })} value={`${s.none}`} />
                                </Box>
                            )
                        })()}
                        {filterChips}
                        <TableContainer sx={{ ...tableStyles.container, marginTop: '14px' }}>
                            <Table size="small">
                                <TableHead sx={tableStyles.head}>
                                    <TableRow>
                                        <TableCell>#</TableCell>
                                        <TableCell>{formatMessage({ id: 'student' })}</TableCell>
                                        {week ? (
                                            <>
                                                <TableCell>{formatMessage({ id: 'practicePs1' })}</TableCell>
                                                <TableCell>{formatMessage({ id: 'practicePs2' })}</TableCell>
                                            </>
                                        ) : (
                                            WEEKS.map((w) => <TableCell key={w}>{w} {formatMessage({ id: 'week' })}</TableCell>)
                                        )}
                                        <TableCell sx={{ cursor: 'pointer' }} onClick={() => setSortAsc((v) => !v)}>
                                            {formatMessage({ id: 'practiceAttendancePct' })} {sortAsc ? '▲' : '▼'}
                                        </TableCell>
                                        <TableCell align="right">{formatMessage({ id: 'details' })}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sorted(selected.recs).map((r, i) => (
                                        <TableRow key={r.key} sx={tableStyles.row}>
                                            <TableCell>{i + 1}</TableCell>
                                            <TableCell>{r.name}</TableCell>
                                            {week
                                                ? r.marks.map((v, idx) => <TableCell key={idx}><Mark on={v} /></TableCell>)
                                                : WEEKS.map((w, wi) => (
                                                    <TableCell key={w}>
                                                        <Box sx={{ display: 'flex', gap: '6px' }}>
                                                            <Mark on={r.marks[wi * 2]} />
                                                            <Mark on={r.marks[wi * 2 + 1]} />
                                                        </Box>
                                                    </TableCell>
                                                ))}
                                            <TableCell><Pct pct={r.pct} /></TableCell>
                                            <TableCell align="right"><Open onClick={() => openStudent(r)} /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', gap: 1, marginBottom: '14px' }}>
                            {(['mentors', 'students', 'top'] as const).map((t) => (
                                <Box key={t} sx={chip(tab === t)} onClick={() => setTab(t)}>
                                    {formatMessage({ id: `practiceTab_${t}` })}
                                </Box>
                            ))}
                        </Box>
                        {loading && <LinearProgress sx={{ marginBottom: 2 }} />}

                        {tab === 'mentors' && (
                            <TableContainer sx={tableStyles.container}>
                                <Table size="small">
                                    <TableHead sx={tableStyles.head}>
                                        <TableRow>
                                            <TableCell>#</TableCell>
                                            <TableCell>{formatMessage({ id: 'colMentor' })}</TableCell>
                                            <TableCell align="center">{formatMessage({ id: 'colStudents' })}</TableCell>
                                            {week ? (
                                                <>
                                                    <TableCell align="center">{formatMessage({ id: 'practicePs1' })}</TableCell>
                                                    <TableCell align="center">{formatMessage({ id: 'practicePs2' })}</TableCell>
                                                    <TableCell align="center">{formatMessage({ id: 'practiceFull' })}</TableCell>
                                                    <TableCell align="center">{formatMessage({ id: 'practiceNoneWeek' })}</TableCell>
                                                </>
                                            ) : (
                                                <>
                                                    <TableCell align="center">{formatMessage({ id: 'practiceAvg' })}</TableCell>
                                                    <TableCell align="center">{formatMessage({ id: 'practiceNone' })}</TableCell>
                                                    <TableCell align="center">{formatMessage({ id: 'practiceFull' })}</TableCell>
                                                </>
                                            )}
                                            <TableCell align="right">{formatMessage({ id: 'details' })}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {[...mentors.map((m) => ({ id: m.mentorId, name: m.name, recs: m.recs })), { id: 0, name: formatMessage({ id: 'practiceTotal' }), recs: records }].map((m, i, arr) => {
                                            const s = summary(m.recs)
                                            const isTotal = i === arr.length - 1
                                            return (
                                                <TableRow key={m.id} sx={{ ...tableStyles.row, ...(isTotal ? { '& .MuiTableCell-root': { fontWeight: 700, background: ui.surface } } : {}) }}>
                                                    <TableCell>{isTotal ? '' : i + 1}</TableCell>
                                                    <TableCell>{m.name}</TableCell>
                                                    <TableCell align="center">{s.n}</TableCell>
                                                    {week ? (
                                                        <>
                                                            <TableCell align="center"><Pct pct={s.ps1.pct} sub={`${s.ps1.hit}`} /></TableCell>
                                                            <TableCell align="center"><Pct pct={s.ps2.pct} sub={`${s.ps2.hit}`} /></TableCell>
                                                            <TableCell align="center">{s.full}</TableCell>
                                                            <TableCell align="center">{s.none}</TableCell>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <TableCell align="center"><Pct pct={s.avg} /></TableCell>
                                                            <TableCell align="center">{s.none}</TableCell>
                                                            <TableCell align="center">{s.full}</TableCell>
                                                        </>
                                                    )}
                                                    <TableCell align="right">{!isTotal && <Open onClick={() => setMentorId(m.id)} />}</TableCell>
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {tab === 'students' && (
                            <>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
                                    <TextField size="small" placeholder={formatMessage({ id: 'searchByStudentName' })} value={search} onChange={(e) => setSearch(e.target.value)} />
                                    {filterChips}
                                </Box>
                                <TableContainer sx={tableStyles.container}>
                                    <Table size="small">
                                        <TableHead sx={tableStyles.head}>
                                            <TableRow>
                                                <TableCell>#</TableCell>
                                                <TableCell>{formatMessage({ id: 'student' })}</TableCell>
                                                <TableCell>{formatMessage({ id: 'headGroupMentor' })}</TableCell>
                                                <TableCell>{formatMessage({ id: 'practicePs' })}</TableCell>
                                                <TableCell sx={{ cursor: 'pointer' }} onClick={() => setSortAsc((v) => !v)}>
                                                    {formatMessage({ id: 'practiceAttendancePct' })} {sortAsc ? '▲' : '▼'}
                                                </TableCell>
                                                <TableCell align="right">{formatMessage({ id: 'details' })}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {sorted(records).map((r, i) => (
                                                <TableRow key={r.key} sx={tableStyles.row}>
                                                    <TableCell>{i + 1}</TableCell>
                                                    <TableCell>{r.name}</TableCell>
                                                    <TableCell sx={{ color: ui.sub, fontSize: '12.5px' }}>{groupLabel(r.groupId)}</TableCell>
                                                    <TableCell>{marksCell(r)}</TableCell>
                                                    <TableCell><Pct pct={r.pct} /></TableCell>
                                                    <TableCell align="right"><Open onClick={() => openStudent(r)} /></TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}

                        {tab === 'top' && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
                                <TopList title={formatMessage({ id: 'practiceTop10' })} recs={[...records].sort(byAttendance).slice(0, 10)} onOpen={openStudent} sub={(r) => groupLabel(r.groupId)} />
                                {mentors.map((m) => (
                                    <TopList key={m.mentorId} title={formatMessage({ id: 'practiceTop3' }, { name: m.name })} recs={[...m.recs].sort(byAttendance).slice(0, 3)} onOpen={openStudent} />
                                ))}
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    )
}

const Mark: React.FC<{ on?: boolean; gap?: boolean }> = ({ on, gap }) => (
    <Box
        component="span"
        sx={{
            width: 22,
            height: 22,
            borderRadius: '6px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: '13px',
            marginRight: gap ? '6px' : 0,
            background: on ? LEVEL_COLORS.green.bg : LEVEL_COLORS.red.bg,
            color: on ? LEVEL_COLORS.green.text : LEVEL_COLORS.red.text,
        }}
    >
        {on ? '✓' : '–'}
    </Box>
)

const Pct: React.FC<{ pct: number; sub?: string }> = ({ pct, sub }) => {
    const c = pctColor(pct)
    return (
        <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ padding: '2px 10px', borderRadius: '100px', background: c.bg, color: c.text, fontWeight: 700, fontSize: '12.5px' }}>{pct}%</Box>
            {sub != null && <Box sx={{ fontSize: '11.5px', color: ui.muted, marginTop: '2px' }}>{sub}</Box>}
        </Box>
    )
}

const Open: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <IconButton onClick={onClick} sx={{ background: ui.pinkSoft, borderRadius: '10px' }}>
        <ArrowForwardRounded sx={{ color: ui.pink, fontSize: 18 }} />
    </IconButton>
)

const Card: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <Box sx={{ border: `1px solid ${ui.border}`, borderRadius: '14px', padding: '12px 14px' }}>
        <Box sx={{ fontSize: '12px', color: ui.sub }}>{label}</Box>
        <Box sx={{ fontSize: '22px', fontWeight: 800, color: ui.ink, marginTop: '4px' }}>{value}</Box>
    </Box>
)

const TopList: React.FC<{ title: string; recs: Rec[]; onOpen: (r: Rec) => void; sub?: (r: Rec) => string }> = ({ title, recs, onOpen, sub }) => (
    <Box sx={{ border: `1px solid ${ui.border}`, borderRadius: '14px', padding: '14px 16px' }}>
        <Box sx={{ fontWeight: 700, marginBottom: '8px' }}>{title}</Box>
        {recs.map((r, i) => (
            <Box
                key={r.key}
                onClick={() => onOpen(r)}
                sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, padding: '6px 0', borderBottom: `1px solid ${ui.border}`, cursor: 'pointer', fontSize: '13px' }}
            >
                <Box>
                    <b>{i + 1}.</b> {r.name}
                    {sub && <Box sx={{ fontSize: '11.5px', color: ui.muted }}>{sub(r)}</Box>}
                </Box>
                <Pct pct={r.pct} />
            </Box>
        ))}
    </Box>
)

const chip = (active: boolean) => ({
    padding: '7px 14px',
    borderRadius: '12px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: active ? 600 : 400,
    color: active ? ui.pink : ui.sub,
    background: active ? ui.pinkSoft : 'transparent',
    border: `1px solid ${active ? 'transparent' : ui.border}`,
})

const page = { background: ui.card, border: `1px solid ${ui.border}`, borderRadius: '16px', padding: '24px', minHeight: '80vh' }
const cards = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1.5, marginBottom: '14px' }

export default HeadPracticePage
